'use client';

import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Text,
} from '@chakra-ui/react';
import dynamic from 'next/dynamic';
import React, { useCallback, useState, useTransition } from 'react';

import { ReactComponent as CloseBlackIcon } from '@/app/_icons/CkClose_black.svg';
import { ReactComponent as SearchWhiteIcon } from '@/app/_icons/CkSearchWhite.svg';
import { BlogItem } from '@/types/blog/blog.types';
import { NewCar } from '@/types/new-cars/new-cars.types';

import './GlobalSearch.scss';

const InstantSearch = dynamic(() => import('./InstantSearch'));

interface Props {
  config: {
    search_quick_links: {
      title: string;
      url: string;
    }[];
  };
  newCars: NewCar[];
  latestBlogPosts: BlogItem[];
}

const GlobalSearch = ({ config, newCars, latestBlogPosts }: Props) => {
  const [hasSearched, setHasSearched] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [isPending, startTransition] = useTransition();

  const handleSelectItem = () => {
    startTransition(() => {
      setIsDrawerOpen(false);
    });
  };

  const queryHook = useCallback(
    (query: string, searchFn: (query: string) => any) => {
      if (query && query.length) {
        searchFn(query);
        setHasSearched(true);
      } else {
        setHasSearched(false);
      }
    },
    [setHasSearched],
  );

  return (
    <>
      <div className="headerGlobalSearchBtnDesk">
        <button
          onClick={() => {
            startTransition(() => {
              setIsDrawerOpen(true);
              setHasSearched(false);
            });
          }}
          className="searchDrawerBtn"
          style={{
            borderRadius: 0,
          }}
          aria-label="Open Search Drawer"
        >
          <div>
            <SearchWhiteIcon className="searchIcon" />
            <span>Search</span>
          </div>
        </button>
      </div>
      <div className="headerGlobalSearchBtnMob">
        <button
          onClick={() => {
            startTransition(() => {
              setIsDrawerOpen(true);
              setHasSearched(false);
            });
          }}
          className="searchDrawerBtn"
          style={{ borderRadius: 0 }}
          aria-label="Open Search Drawer"
        >
          {isPending ? (
            '...'
          ) : (
            <div>
              <SearchWhiteIcon className="searchIcon" />
            </div>
          )}
        </button>
      </div>
      <Drawer
        isOpen={isDrawerOpen}
        placement="right"
        onClose={() => {
          startTransition(() => {
            setIsDrawerOpen(false);
          });
        }}
        preserveScrollBarGap
        size="md"
        id="globalSearchWrap"
      >
        <DrawerOverlay />
        <DrawerContent>
          {isDrawerOpen ? (
            <>
              <button
                onClick={() => {
                  startTransition(() => {
                    setIsDrawerOpen(false);
                    setHasSearched(false);
                  });
                }}
                className="searchDrawerCloseBtn"
                style={{ borderRadius: 0 }}
              >
                <div>
                  <CloseBlackIcon className="closeIcon" />
                  <Text className="hideMobileClose">Close</Text>
                </div>
              </button>
              <DrawerBody
                paddingX={{ base: 6, sm: 10 }}
                paddingTop={{ base: 0 }}
                paddingBottom={{ base: 6, sm: 8 }}
              >
                <div>
                  <InstantSearch
                    hasSearched={hasSearched}
                    onSelectItem={handleSelectItem}
                    queryHook={queryHook}
                    config={config}
                    newCars={newCars}
                    latestBlogPosts={latestBlogPosts}
                  />
                </div>
              </DrawerBody>
            </>
          ) : null}
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default GlobalSearch;
